import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody
} from '../ui/Dialog';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import { Spinner } from '../ui/Spinner';
import { GetCompanyPayslipBranding } from '@/api/Payroll/payslipBranding';

// --- Branding interfaces ---

interface LogoSettings {
  hasLogo: boolean;
  logoUrl: string;
  logoPosition: string;
  logoSize: string;
}

interface HeaderSettings {
  companyName: string;
  headerText: string;
  showDate: boolean;
  showPeriod: boolean;
  headerColor: string;
  headerBackgroundColor: string;
}

interface FooterSettings {
  footerText: string;
  includeDisclaimer: boolean;
  disclaimerText: string;
  showHelpContact: boolean;
  helpContact: string;
  footerColor: string;
}

interface LayoutSettings {
  template: string;
  colorScheme: string;
  fontSize: string;
  showCompanyAddress: boolean;
  showEmployeePhoto: boolean;
  paperSize: string;
  margins: string;
}

interface CustomizationSettings {
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  borderStyle: string;
  showWatermark: boolean;
  watermarkText: string;
}

interface BrandingSettings {
  logoSettings: LogoSettings;
  headerSettings: HeaderSettings;
  footerSettings: FooterSettings;
  layoutSettings: LayoutSettings;
  customization: CustomizationSettings;
}

const defaultBranding: BrandingSettings = {
  logoSettings: { hasLogo: false, logoUrl: '', logoPosition: 'left', logoSize: 'medium' },
  headerSettings: { companyName: 'Company Name', headerText: 'Monthly Payslip', showDate: true, showPeriod: true, headerColor: '#1f2937', headerBackgroundColor: '#f9fafb' },
  footerSettings: { footerText: 'Confidential - For Employee Use Only', includeDisclaimer: false, disclaimerText: '', showHelpContact: false, helpContact: '', footerColor: '#6b7280' },
  layoutSettings: { template: 'standard', colorScheme: 'professional', fontSize: 'medium', showCompanyAddress: true, showEmployeePhoto: false, paperSize: 'A4', margins: 'normal' },
  customization: { primaryColor: '#2563eb', accentColor: '#059669', fontFamily: 'Inter', borderStyle: 'subtle', showWatermark: false, watermarkText: 'CONFIDENTIAL' }
};

/** Helper: reads a property from a parsed JSON object, checking both camelCase and PascalCase keys. */
const bp = (obj: any, camelKey: string): any => {
  if (!obj) return undefined;
  const val = obj[camelKey];
  if (val !== undefined) return val;
  // Try PascalCase: capitalize first letter
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
  return obj[pascalKey];
};

// --- Helpers ---

const lightenColor = (hex: string, amount: number): string => {
  try {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    // Match C# LightenColor: blend toward white by amount
    const lr = Math.min(255, Math.round(r + (255 - r) * amount));
    const lg = Math.min(255, Math.round(g + (255 - g) * amount));
    const lb = Math.min(255, Math.round(b + (255 - b) * amount));
    return `#${(lr << 16 | lg << 8 | lb).toString(16).padStart(6, '0')}`;
  } catch {
    return hex;
  }
};

/** Returns true if the hex color is dark (needs white text), false if light (needs dark text) */
const isDarkColor = (hex: string): boolean => {
  try {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    // Relative luminance (ITU-R BT.709)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.6;
  } catch {
    return false;
  }
};

const getFontSize = (size: string) => {
  switch (size) {
    case 'small': return { base: '12px', header: '20px', title: '16px' };
    case 'large': return { base: '16px', header: '28px', title: '20px' };
    default: return { base: '14px', header: '24px', title: '18px' };
  }
};

// --- Shared payslip data interface ---

export interface PayslipData {
  id: number;
  name: string;
  employeeId: string;
  department?: string;
  role?: string;
  basicSalary: number;
  variablePay: number;
  deductions: number;
  netPay: number;
  adjustments?: number;
  adjustmentAdditions?: number;
  adjustmentDeductions?: number;
  /** FDL 33/2021 Art. 19/20/21 — monetized overtime pay. */
  overtimePay?: number;
  /** KSA Art. 107 actual wage (basic + fixed allowances); >0 signals a KSA payslip. */
  overtimeActualWage?: number | null;
  /** True basic monthly wage used for the OT hourly base (basic ÷ 208). */
  overtimeBasicWage?: number | null;
  /** OT hours classified regular (UAE ×1.25 tier). */
  regularOvertimeHours?: number;
  /** OT hours classified premium (UAE ×1.50 tier). */
  premiumOvertimeHours?: number;
  /** GPSSA FL 7/1999 / ADPF AD Law 2/2000 — employee pension contribution. */
  pensionDeduction?: number;
  /** KSA SI Law 1421/2000 Art. 19 — GOSI ANI employer share (HR-only display). */
  gosiAniEmployer?: number | null;
  /** KSA SI Law 1421/2000 Art. 19 — GOSI ANI employee share (HR-only display). */
  gosiAniEmployee?: number | null;
  /** KSA SI Law 1421/2000 — GOSI Occupational Hazards employer share (HR-only display). */
  gosiOhEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED unemployment insurance employer share (HR-only display). */
  gosiSanedEmployer?: number | null;
  /** KSA SI Law 1421/2000 — GOSI SANED unemployment insurance employee share (HR-only display). */
  gosiSanedEmployee?: number | null;
  /** Council of Health Insurance (CCHI) — employee share for non-Saudis. */
  cchiEmployeeShare?: number | null;
  /**
   * Hourly pay §8 — present only for an hourly employee: the pension / GOSI base this payslip's deduction was
   * computed on (the month's approved hourly earnings). Absent for salaried employees, whose payslip is unchanged.
   */
  hourlyStatutoryBase?: number | null;
  /** Controls whether variable pay is included (matches PDF generation logic) */
  includeVariablePay?: boolean;
  /** Controls whether adjustment additions are included (matches PDF generation logic) */
  includeAdjustmentAdditions?: boolean;
  /** Controls whether adjustment deductions are included (matches PDF generation logic) */
  includeAdjustmentDeductions?: boolean;
  payslipStatus: string;
  payslipUrl?: string | null;
  payrollDate?: string;
  paymentDate?: string;
  generatedDate?: string;
  createdOn?: string;
  payrollCycle?: string;
}

export interface PayslipPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  payslip: PayslipData | null;
  payrollCycle: string;
  currencySymbol?: string;
  onDownload?: (payslipId: number) => Promise<void>;
  onEmail?: (payslipId: number) => Promise<void>;
  onGenerate?: () => void | Promise<void>;
  /**
   * When true, the modal renders the "Employer Obligations (computed)" sub-section
   * (GOSI ANI/OH/SANED employer amounts plus the Mowazaf-doesn't-verify disclaimer).
   * Default is false — the employee-facing payslip never shows employer obligations.
   */
  isHrView?: boolean;
}

/**
 * Small circled-i that surfaces a statutory law reference on hover, instead of
 * printing it inline under every line. Keeps the on-screen payslip uncluttered
 * while leaving the citation one hover away (native title tooltip — no deps,
 * degrades gracefully in print).
 */
const LawRefHint: React.FC<{ refText: string; color?: string }> = ({ refText, color }) => (
  <span
    title={refText}
    aria-label={refText}
    className="ms-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[8px] font-semibold leading-none align-middle cursor-help opacity-60 hover:opacity-100 print:hidden"
    style={color ? { borderColor: color, color } : undefined}
  >
    {'i'}
  </span>
);

const PayslipPreviewModal: React.FC<PayslipPreviewModalProps> = ({
  isOpen,
  onClose,
  payslip,
  payrollCycle,
  currencySymbol = 'AED',
  onDownload,
  onEmail,
  onGenerate,
  isHrView = false,
}) => {
  const { t } = useTranslation();
  const [otBreakdownOpen, setOtBreakdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBrandingLoading, setIsBrandingLoading] = useState(false);
  const [branding, setBranding] = useState<BrandingSettings>(defaultBranding);

  useEffect(() => {
    if (isOpen) {
      loadBrandingSettings();
    }
  }, [isOpen]);

  const loadBrandingSettings = async () => {
    setIsBrandingLoading(true);
    try {
      const result = await GetCompanyPayslipBranding();
      if (result && result.status === 200 && result.payload && !result.errors?.length) {
        const data = result.payload;

        const parseSetting = (val: unknown) => typeof val === 'string' ? JSON.parse(val) : val;
        const pLogo = parseSetting(data.logoSettings);
        const pHeader = parseSetting(data.headerSettings);
        const pFooter = parseSetting(data.footerSettings);
        const pLayout = parseSetting(data.layoutSettings);
        const pCustom = parseSetting(data.customization);

        setBranding({
          logoSettings: {
            hasLogo: !!data.logoUrl,
            logoUrl: data.logoUrl || '',
            logoPosition: bp(pLogo, 'logoPosition') || 'left',
            logoSize: bp(pLogo, 'logoSize') || 'medium'
          },
          headerSettings: {
            companyName: bp(pHeader, 'companyName') || 'Company Name',
            headerText: bp(pHeader, 'headerText') || 'Monthly Payslip',
            showDate: bp(pHeader, 'showDate') ?? true,
            showPeriod: bp(pHeader, 'showPeriod') ?? true,
            headerColor: bp(pHeader, 'headerColor') || '#1f2937',
            headerBackgroundColor: bp(pHeader, 'headerBackgroundColor') || '#f9fafb'
          },
          footerSettings: {
            footerText: bp(pFooter, 'footerText') || 'Confidential - For Employee Use Only',
            includeDisclaimer: bp(pFooter, 'includeDisclaimer') ?? false,
            disclaimerText: bp(pFooter, 'disclaimerText') || '',
            showHelpContact: bp(pFooter, 'showHelpContact') ?? false,
            helpContact: bp(pFooter, 'helpContact') || '',
            footerColor: bp(pFooter, 'footerColor') || '#6b7280'
          },
          layoutSettings: {
            template: bp(pLayout, 'template') || 'standard',
            colorScheme: bp(pLayout, 'colorScheme') || 'professional',
            fontSize: bp(pLayout, 'fontSize') || 'medium',
            showCompanyAddress: bp(pLayout, 'showCompanyAddress') ?? true,
            showEmployeePhoto: bp(pLayout, 'showEmployeePhoto') ?? false,
            paperSize: bp(pLayout, 'paperSize') || 'A4',
            margins: bp(pLayout, 'margins') || 'normal'
          },
          customization: {
            primaryColor: bp(pCustom, 'primaryColor') || '#2563eb',
            accentColor: bp(pCustom, 'accentColor') || '#059669',
            fontFamily: bp(pCustom, 'fontFamily') || 'Inter',
            borderStyle: bp(pCustom, 'borderStyle') || 'subtle',
            showWatermark: bp(pCustom, 'showWatermark') ?? false,
            watermarkText: bp(pCustom, 'watermarkText') || 'CONFIDENTIAL'
          }
        });
      }
    } catch (error) {
      console.error('Failed to load branding settings:', error);
    } finally {
      setIsBrandingLoading(false);
    }
  };

  if (!isOpen || !payslip) return null;

  // --- Derived values (must match PDF generation logic in PayslipBrandingService.cs) ---

  const includeVariablePay = payslip.includeVariablePay !== false;
  const includeAdjAdditions = payslip.includeAdjustmentAdditions !== false;
  const includeAdjDeductions = payslip.includeAdjustmentDeductions !== false;

  const allowances = includeVariablePay ? (payslip.variablePay || 0) : 0;
  const overtimePay = payslip.overtimePay ?? 0;
  // Country-aware OT breakdown. KSA populates overtimeActualWage; UAE leaves it null.
  // Sub-rows are display-only reconstructions; the headline total is the stored overtimePay
  // (server-side, unrounded intermediates), so a sub-row product may differ by a cent.
  // The 208 divisor must stay in sync with PayrollComplianceConstants.HoursPerMonth.
  const HOURS_PER_MONTH = 208;
  const isKsaOvertime = (payslip.overtimeActualWage ?? 0) > 0;
  const otRegularHours = payslip.regularOvertimeHours ?? 0;
  const otPremiumHours = payslip.premiumOvertimeHours ?? 0;
  const otTotalHours = otRegularHours + otPremiumHours;
  const otBasicHourly = (payslip.overtimeBasicWage ?? 0) / HOURS_PER_MONTH;
  const otActualHourly = (payslip.overtimeActualWage ?? 0) / HOURS_PER_MONTH;
  const otRatePerHour = otActualHourly + 0.5 * otBasicHourly;
  const otCitation = isKsaOvertime
    ? t('payroll.payslip.overtime.citationKsa', 'KSA Labor Law Art. 107')
    : t('payroll.payslip.overtime.citationUae', 'FDL 33/2021 Art. 19');
  const pensionDeduction = payslip.pensionDeduction ?? 0;
  const adjAdditions = includeAdjAdditions ? (payslip.adjustmentAdditions ?? 0) : 0;
  const adjDeductions = includeAdjDeductions ? (payslip.adjustmentDeductions ?? 0) : 0;
  const adjustments = adjAdditions - adjDeductions;
  const netPay = payslip.netPay || 0;
  // CCHI is a standalone employee deduction (unlike GOSI ANI/SANED, which the backend
  // rolls into pensionDeduction), so subtract it here too or Total won't reconcile to Net.
  const total = (payslip.basicSalary || 0) + overtimePay + allowances + adjustments - pensionDeduction - (payslip.cchiEmployeeShare ?? 0);
  const netPayBgColor = lightenColor(branding.headerSettings.headerBackgroundColor, 0.3);
  const netPayDark = isDarkColor(netPayBgColor);
  const netPayTextColor = netPayDark ? '#ffffff' : '#1f2937';
  const netPaySubTextColor = netPayDark ? '#e0e7ff' : '#6b7280';
  const fontSize = getFontSize(branding.layoutSettings.fontSize);
  const effectivePayrollDate = payslip.payrollDate || payslip.paymentDate;
  const formattedPaymentDate = effectivePayrollDate
    ? new Date(effectivePayrollDate).toLocaleDateString('en-GB')
    : new Date().toLocaleDateString('en-GB');
  const displayCycle = payslip.payrollCycle || payrollCycle;

  // Employer obligations (HR-only, never shown on employee payslip).
  // Per spec: Mowazaf computes these; actual GOSI filing happens externally.
  const gosiAniEmployer = payslip.gosiAniEmployer ?? null;
  const gosiOhEmployer = payslip.gosiOhEmployer ?? null;
  const gosiSanedEmployer = payslip.gosiSanedEmployer ?? null;
  const cchiEmployeeShare = payslip.cchiEmployeeShare ?? null;
  const hasEmployerObligations =
    isHrView && (gosiAniEmployer != null || gosiOhEmployer != null || gosiSanedEmployer != null);
  const employerSectionTitle = t('gosi.payslip.employer_section_title', 'Employer Obligations (computed)');
  // Disclaimer strings MUST match KsaGosiPanel.tsx exactly — same i18n keys, same fallbacks.
  // If the locale file is missing these keys, the panel and the payslip must still display
  // identical legal text.
  const employerDisclaimerTitle = t('gosi.panel.disclaimer.title', 'Computed obligations only');
  const employerDisclaimerBody = t(
    'gosi.panel.disclaimer.body',
    'These figures are computed from payroll. Actual payment is filed monthly through GOSI Online. We do not verify the transfer.'
  );
  const gosiAniLabel = t('gosi.payslip.aniEmployer', 'GOSI ANI (Employer)');
  const gosiOhLabel = t('gosi.payslip.ohEmployer', 'GOSI OH (Employer)');
  const gosiSanedLabel = t('gosi.payslip.sanedEmployer', 'GOSI SANED (Employer)');

  // Employee-side KSA social insurance (visible on the employee-facing payslip).
  // When both ANI and SANED employee amounts are present, the employee is a Saudi
  // national on KSA payroll — show the SANED breakdown + a "Total social insurance"
  // subtotal in place of the generic UAE "Pension Contribution" line. The backend
  // already rolls ANI_emp + SANED_emp into payslip.pensionDeduction so the Total
  // line and net-pay math stay correct without any extra subtraction here.
  const gosiAniEmployee = payslip.gosiAniEmployee ?? null;
  const gosiSanedEmployee = payslip.gosiSanedEmployee ?? null;
  const showSaudiSocialInsurance = gosiAniEmployee != null && gosiSanedEmployee != null;
  const totalSocialInsurance = showSaudiSocialInsurance
    ? (gosiAniEmployee as number) + (gosiSanedEmployee as number)
    : 0;
  const gosiAniEmployeeLabel = t('gosi.payslip.aniEmployee', 'GOSI ANI (Employee)');
  const gosiSanedEmployeeLabel = t('gosi.payslip.sanedEmployee', 'GOSI SANED (Employee)');
  const totalSocialInsuranceLabel = t('gosi.payslip.total_social_insurance', 'Total social insurance');

  // Hourly pay §8 — an hourly employee has no salary component, so the payslip says what the pension / GOSI
  // deduction above was computed on. Only when the server sent the base AND a statutory line is shown.
  const hourlyStatutoryBase = payslip.hourlyStatutoryBase ?? null;
  const showHourlyStatutoryBase =
    hourlyStatutoryBase != null && (showSaudiSocialInsurance || pensionDeduction > 0);
  const hourlyStatutoryBaseCaption = showHourlyStatutoryBase
    ? t('payroll.payslip.hourlyStatutoryBase', {
        amount: `${currencySymbol} ${(hourlyStatutoryBase as number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      })
    : '';
  const hourlyStatutoryBaseHint = t('payroll.payslip.hourlyStatutoryBaseHint');

  // --- Handlers ---

  const handleDownload = async () => {
    if (!payslip || !onDownload) return;
    setIsLoading(true);
    try {
      await onDownload(payslip.id);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmail = async () => {
    if (!payslip || !onEmail) return;
    setIsLoading(true);
    try {
      await onEmail(payslip.id);
    } catch (error) {
      console.error('Email failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fmtNum = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

  const handlePrint = (): void => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) return;

    const headerBg = branding.headerSettings.headerBackgroundColor;
    const headerTextColor = branding.headerSettings.headerColor;
    const accent = branding.customization.accentColor;
    const footerClr = branding.footerSettings.footerColor;

    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip - ${payslip.name}</title>
          <style>
            body { margin: 0; padding: 0; font-family: ${branding.customization.fontFamily}, Arial, sans-serif; font-size: 10px; color: #424242; }
            .payslip-container { max-width: 800px; margin: 0 auto; position: relative; }
            .company-header { padding: 20px 30px; background-color: ${headerBg}; color: ${headerTextColor}; }
            .company-header h1 { margin: 0; font-size: 16px; font-weight: 600; }
            .company-header p { margin: 4px 0 0; font-size: 10px; opacity: 0.9; }
            .company-logo { max-height: 56px; border-radius: 8px; }
            .header-row { display: flex; align-items: center; gap: 16px; }
            .header-row-right { display: flex; align-items: center; justify-content: space-between; }
            .header-center { text-align: center; }
            .content { padding: 30px; }
            .info-section { background: #f0fdf4; border-radius: 8px; padding: 20px; margin-bottom: 24px; }
            .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
            .info-title { font-size: 16px; font-weight: 700; color: #424242; margin: 0 0 12px; }
            .info-subtitle { font-size: 14px; font-weight: 600; color: #424242; margin: 0 0 12px; }
            .info-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 10px; }
            .info-label { color: #4b5563; }
            .info-value { font-weight: 600; color: #424242; }
            .section-title { font-size: 14px; font-weight: 600; color: ${accent}; margin: 0 0 12px; }
            .item-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb; font-size: 10px; }
            .item-label { color: #4b5563; }
            .item-value { font-weight: 600; color: #424242; }
            .total-row { display: flex; justify-content: space-between; padding: 8px 0; border-top: 2px solid #d1d5db; font-size: 10px; font-weight: 600; }
            .adjustment-positive { color: ${accent}; }
            .adjustment-negative { color: #dc2626; }
            .adjustment-row-positive { background-color: #D1FAE5; padding: 8px; border-radius: 4px; display: flex; justify-content: space-between; font-size: 10px; }
            .adjustment-row-negative { background-color: #FEE2E2; padding: 8px; border-radius: 4px; display: flex; justify-content: space-between; font-size: 10px; }
            .net-pay { background: ${netPayBgColor}; border-radius: 8px; padding: 20px; text-align: center; margin: 24px 0; }
            .net-pay h3 { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: ${netPayTextColor}; }
            .net-pay .amount { font-size: 24px; font-weight: bold; color: ${netPayTextColor}; }
            .net-pay p { margin: 4px 0 0; font-size: 9px; color: ${netPaySubTextColor}; }
            .footer { text-align: center; padding-top: 10px; border-top: 1px solid #d1d5db; font-size: 9px; color: ${footerClr}; }
            .footer p { margin: 4px 0; }
            .watermark {
              position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 4rem; font-weight: bold; opacity: 0.06; pointer-events: none; color: ${headerTextColor};
            }
            @media print { body { margin: 0; } .payslip-container { max-width: none; } }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            ${branding.customization.showWatermark && branding.customization.watermarkText
              ? `<div class="watermark">${branding.customization.watermarkText}</div>` : ''}

            <div class="company-header">
              ${(() => {
                const logoPos = branding.logoSettings.logoPosition || 'center';
                const hasLogo = branding.logoSettings.hasLogo && branding.logoSettings.logoUrl;
                const logoHtml = hasLogo ? `<img src="${branding.logoSettings.logoUrl}" alt="Logo" class="company-logo" />` : '';
                const textHtml = `<div><h1>${branding.headerSettings.companyName}</h1>${branding.headerSettings.headerText ? `<p>${branding.headerSettings.headerText}</p>` : ''}</div>`;

                if ((logoPos === 'left' || logoPos === 'top-left') && hasLogo) {
                  return `<div class="header-row">${logoHtml}${textHtml}</div>`;
                }
                if ((logoPos === 'right' || logoPos === 'top-right') && hasLogo) {
                  return `<div class="header-row-right">${textHtml}${logoHtml}</div>`;
                }
                return `<div class="header-center">${logoHtml ? `<div style="margin-bottom:12px;">${logoHtml}</div>` : ''}${textHtml}</div>`;
              })()}
            </div>

            <div class="content">
            <div class="info-section">
              <div class="grid-2">
                <div>
                  <h2 class="info-title">${t('PAYSLIP')}</h2>
                  <div class="info-row"><span class="info-label">${t('Pay Period')}:</span><span class="info-value">${displayCycle}</span></div>
                  <div class="info-row"><span class="info-label">${t('Payment Date')}:</span><span class="info-value">${formattedPaymentDate}</span></div>
                  ${(payslip.generatedDate || payslip.createdOn) ? `<div class="info-row"><span class="info-label">${t('Generated')}:</span><span class="info-value">${new Date(payslip.generatedDate || payslip.createdOn).toLocaleDateString('en-GB')}</span></div>` : ''}
                </div>
                <div>
                  <h3 class="info-subtitle">${t('Employee Details')}</h3>
                  <div class="info-row"><span class="info-label">${t('Full Name')}:</span><span class="info-value">${payslip.name}</span></div>
                  ${payslip.department ? `<div class="info-row"><span class="info-label">${t('Department')}:</span><span class="info-value">${payslip.department}</span></div>` : ''}
                  ${payslip.role ? `<div class="info-row"><span class="info-label">${t('Role')}:</span><span class="info-value">${payslip.role}</span></div>` : ''}
                </div>
              </div>
            </div>

            <div style="margin-bottom: 24px;">
              <h3 class="section-title">${t('Earnings')}</h3>
              <div class="item-row"><span class="item-label">${t('Gross Salary')}</span><span class="item-value">${currencySymbol} ${fmtNum(payslip.basicSalary || 0)}</span></div>
              ${overtimePay > 0 ? `<div class="item-row"><span class="item-label">${t('payroll.payslip.overtimePay', 'Overtime Pay')}<br/><small style="color:#9ca3af;font-size:7px;">${otCitation}</small></span><span class="item-value">${currencySymbol} ${fmtNum(overtimePay)}</span></div>
                <div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.basicHourly', 'Basic hourly wage')}</span><span class="item-value" style="font-size:8px;">${currencySymbol} ${otBasicHourly.toFixed(2)}</span></div>
                ${isKsaOvertime
                  ? `<div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.actualHourly', 'Actual hourly wage')}</span><span class="item-value" style="font-size:8px;">${currencySymbol} ${otActualHourly.toFixed(2)}</span></div>
                     <div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.otRatePerHour', 'OT rate / hour')}</span><span class="item-value" style="font-size:8px;">${currencySymbol} ${otRatePerHour.toFixed(2)}</span></div>
                     <div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.hours', 'Overtime hours')}</span><span class="item-value" style="font-size:8px;">${otTotalHours.toFixed(1)}</span></div>`
                  : `<div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.regularTier', 'Regular OT (×1.25)')}</span><span class="item-value" style="font-size:8px;">${otRegularHours.toFixed(1)} ${t('payroll.payslip.overtime.hoursShort', 'h')} · ${currencySymbol} ${(otRegularHours * otBasicHourly * 1.25).toFixed(2)}</span></div>
                     <div class="item-row" style="padding-inline-start:12px;"><span class="item-label" style="color:#6b7280;font-size:8px;">${t('payroll.payslip.overtime.premiumTier', 'Premium OT (×1.50)')}</span><span class="item-value" style="font-size:8px;">${otPremiumHours.toFixed(1)} ${t('payroll.payslip.overtime.hoursShort', 'h')} · ${currencySymbol} ${(otPremiumHours * otBasicHourly * 1.5).toFixed(2)}</span></div>`}` : ''}
              ${includeVariablePay && allowances > 0 ? `<div class="item-row"><span class="item-label">${t('Variable Pay')}</span><span class="item-value">${currencySymbol} ${fmtNum(allowances)}</span></div>` : ''}
              ${showSaudiSocialInsurance
                ? `<div class="adjustment-row-negative"><span class="adjustment-negative">${gosiAniEmployeeLabel}<br/><small style="font-size:7px;">SI Law 1421/2000 Art. 19</small></span><span class="adjustment-negative" style="font-weight:600;">-${currencySymbol} ${fmtNum(gosiAniEmployee as number)}</span></div>
                   <div class="adjustment-row-negative"><span class="adjustment-negative">${gosiSanedEmployeeLabel}<br/><small style="font-size:7px;">SI Law 1421/2000</small></span><span class="adjustment-negative" style="font-weight:600;">-${currencySymbol} ${fmtNum(gosiSanedEmployee as number)}</span></div>
                   <div class="item-row" style="font-weight:600;background:#fef3f2;"><span class="item-label" style="font-weight:600;">${totalSocialInsuranceLabel}</span><span class="item-value" style="font-weight:600;color:#dc2626;">-${currencySymbol} ${fmtNum(totalSocialInsurance)}</span></div>`
                : (pensionDeduction > 0
                  ? `<div class="adjustment-row-negative"><span class="adjustment-negative">${t('payroll.payslip.pension', 'Pension Contribution')}<br/><small style="font-size:7px;">GPSSA FL 7/1999</small></span><span class="adjustment-negative" style="font-weight:600;">-${currencySymbol} ${fmtNum(pensionDeduction)}</span></div>`
                  : '')}
              ${showHourlyStatutoryBase ? `<div style="padding:2px 8px 6px;font-size:8px;color:#6b7280;">${hourlyStatutoryBaseCaption}</div>` : ''}
              ${cchiEmployeeShare != null && cchiEmployeeShare > 0
                ? `<div class="adjustment-row-negative"><span class="adjustment-negative">${t('payslip.cchiEmployeeShare')}</span><span class="adjustment-negative" style="font-weight:600;">-${currencySymbol} ${fmtNum(cchiEmployeeShare)}</span></div>`
                : ''}
              ${adjAdditions > 0 ? `<div class="adjustment-row-positive"><span class="adjustment-positive">${t('Adjustment Addition')}</span><span class="adjustment-positive" style="font-weight:600;">+${currencySymbol} ${fmtNum(adjAdditions)}</span></div>` : ''}
              ${adjDeductions > 0 ? `<div class="adjustment-row-negative"><span class="adjustment-negative">${t('Adjustment Deduction')}</span><span class="adjustment-negative" style="font-weight:600;">-${currencySymbol} ${fmtNum(adjDeductions)}</span></div>` : ''}
              ${(adjAdditions > 0 || adjDeductions > 0) ? `<div class="item-row"><span style="font-weight:600;color:${adjustments >= 0 ? accent : '#dc2626'};">${t('Adjustment Total')}</span><span style="font-weight:600;color:${adjustments >= 0 ? accent : '#dc2626'};">${adjustments > 0 ? '+' : ''}${currencySymbol} ${fmtNum(adjustments)}</span></div>` : ''}
              <div class="total-row"><span>${t('Total')}</span><span style="color:${accent};">${currencySymbol} ${fmtNum(total)}</span></div>
            </div>

            ${hasEmployerObligations ? `
            <div style="margin-bottom: 24px;">
              <h3 class="section-title">${employerSectionTitle}</h3>
              <div role="note" aria-label="${employerDisclaimerTitle}" style="border: 1px solid #e5e7eb; background: #f9fafb; border-radius: 6px; padding: 10px 12px; margin: 0 0 10px;">
                <div style="font-size: 10px; font-weight: 600; color: #424242;">${employerDisclaimerTitle}</div>
                <div style="font-size: 10px; color: #6b7280; margin-top: 4px;">${employerDisclaimerBody}</div>
              </div>
              ${gosiAniEmployer != null ? `<div class="item-row"><span class="item-label">${gosiAniLabel}<br/><small style="color:#9ca3af;font-size:7px;">SI Law 1421/2000 Art. 19</small></span><span class="item-value">${currencySymbol} ${fmtNum(gosiAniEmployer)}</span></div>` : ''}
              ${gosiOhEmployer != null ? `<div class="item-row"><span class="item-label">${gosiOhLabel}<br/><small style="color:#9ca3af;font-size:7px;">SI Law 1421/2000</small></span><span class="item-value">${currencySymbol} ${fmtNum(gosiOhEmployer)}</span></div>` : ''}
              ${gosiSanedEmployer != null ? `<div class="item-row"><span class="item-label">${gosiSanedLabel}<br/><small style="color:#9ca3af;font-size:7px;">SI Law 1421/2000</small></span><span class="item-value">${currencySymbol} ${fmtNum(gosiSanedEmployer)}</span></div>` : ''}
            </div>
            ` : ''}

            <div class="net-pay">
              <h3>${t('Net Pay')}</h3>
              <div class="amount">${currencySymbol} ${fmtNum(netPay)}</div>
              <p>${t('Amount to be credited to your bank account')}</p>
            </div>

            <div class="footer">
              ${branding.footerSettings.footerText ? `<p>${branding.footerSettings.footerText}</p>` : ''}
              ${branding.footerSettings.includeDisclaimer && branding.footerSettings.disclaimerText
                ? `<p style="font-style: italic; font-size: 8px;">${branding.footerSettings.disclaimerText}</p>` : ''}
              ${branding.footerSettings.showHelpContact && branding.footerSettings.helpContact
                ? `<p>${t('For queries, contact')}: ${branding.footerSettings.helpContact}</p>` : ''}
              <p style="margin-top: 4px;">${t('Generated on')} ${new Date().toLocaleDateString('en-GB')} ${t('at')} ${new Date().toLocaleTimeString('en-GB')}</p>
            </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.addEventListener('afterprint', () => printWindow.close());
    printWindow.print();
  };

  // --- Render ---

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="xl" hideCloseButton>
        {/* Modal Header */}
        <DialogHeader className="flex flex-row items-center justify-between bg-muted">
          <div className="flex items-center gap-3 rtl:space-x-reverse">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-success" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{t('Payslip Preview')}</h2>
              <p className="text-sm text-muted-foreground">
                {payslip.name} &bull; {displayCycle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rtl:space-x-reverse">
            <Button
              variant="outline"
              size="sm"
              iconName="Printer"
              onClick={handlePrint}
              disabled={isLoading || isBrandingLoading}
            >
              {t('Print')}
            </Button>
            {onDownload && (
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={handleDownload}
                disabled={isLoading || isBrandingLoading}
              >
                {t('Download')}
              </Button>
            )}
            {onEmail && (
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={handleEmail}
                disabled={isLoading || isBrandingLoading}
              >
                {t('Email')}
              </Button>
            )}
            {onGenerate && (
              <Button
                size="sm"
                iconName="FileText"
                onClick={async () => {
                  setIsLoading(true);
                  try { await onGenerate(); } finally { setIsLoading(false); }
                }}
                disabled={isLoading || isBrandingLoading}
              >
                {t('Generate')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </DialogHeader>

        {/* Payslip Content */}
        <DialogBody className="overflow-y-auto max-h-[calc(90vh-120px)]">
          {isBrandingLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" />
              <span className="ms-2 text-muted-foreground">{t('Loading branding...')}</span>
            </div>
          ) : (
            <div id="payslip-content" className="bg-card border border-border rounded-xl mx-auto max-w-2xl relative overflow-hidden">
              {/* Company Header - colored banner matching PDF */}
              <div
                className="px-8 py-5 mb-0"
                style={{ backgroundColor: branding.headerSettings.headerBackgroundColor }}
              >
                {(() => {
                  const logoPos = branding.logoSettings.logoPosition || 'center';
                  const hasLogo = branding.logoSettings.hasLogo && branding.logoSettings.logoUrl;
                  const logoEl = hasLogo
                    ? <img src={branding.logoSettings.logoUrl} alt="Logo" className="h-14 rounded-lg" />
                    : null;
                  const textEl = (
                    <div>
                      <h1 className="text-xl font-semibold" style={{ color: branding.headerSettings.headerColor }}>
                        {branding.headerSettings.companyName}
                      </h1>
                      {branding.headerSettings.headerText && (
                        <p className="text-sm mt-0.5" style={{ color: branding.headerSettings.headerColor, opacity: 0.9 }}>
                          {branding.headerSettings.headerText}
                        </p>
                      )}
                    </div>
                  );

                  if (logoPos === 'center' || logoPos === 'top-center') {
                    return (
                      <div className="text-center">
                        {logoEl && <div className="flex justify-center mb-3">{logoEl}</div>}
                        {textEl}
                      </div>
                    );
                  }
                  if ((logoPos === 'left' || logoPos === 'top-left') && hasLogo) {
                    return (
                      <div className="flex items-center gap-4">
                        {logoEl}
                        {textEl}
                      </div>
                    );
                  }
                  if ((logoPos === 'right' || logoPos === 'top-right') && hasLogo) {
                    return (
                      <div className="flex items-center justify-between">
                        {textEl}
                        {logoEl}
                      </div>
                    );
                  }
                  return <div className="text-center">{textEl}</div>;
                })()}
              </div>

              <div className="p-8">
              {/* Payslip Info Section - matches PDF two-column layout */}
              <div className="rounded-lg p-5 mb-8" style={{ backgroundColor: '#f0fdf4' }}>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h2 className="text-base font-bold mb-3" style={{ color: '#424242' }}>
                      {t('PAYSLIP')}
                    </h2>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: '#4b5563' }}>{t('Pay Period')}:</span>
                        <span className="font-semibold" style={{ color: '#424242' }}>{displayCycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: '#4b5563' }}>{t('Payment Date')}:</span>
                        <span className="font-semibold" style={{ color: '#424242' }}>
                          {formattedPaymentDate}
                        </span>
                      </div>
                      {(payslip.generatedDate || payslip.createdOn) && (
                        <div className="flex justify-between">
                          <span style={{ color: '#4b5563' }}>{t('Generated')}:</span>
                          <span className="font-semibold" style={{ color: '#424242' }}>
                            {new Date(payslip.generatedDate || payslip.createdOn).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: '#424242' }}>
                      {t('Employee Details')}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: '#4b5563' }}>{t('Full Name')}:</span>
                        <span className="font-semibold" style={{ color: '#424242' }}>{payslip.name}</span>
                      </div>
                      {payslip.department && (
                        <div className="flex justify-between">
                          <span style={{ color: '#4b5563' }}>{t('Department')}:</span>
                          <span className="font-semibold" style={{ color: '#424242' }}>{payslip.department}</span>
                        </div>
                      )}
                      {payslip.role && (
                        <div className="flex justify-between">
                          <span style={{ color: '#4b5563' }}>{t('Role')}:</span>
                          <span className="font-semibold" style={{ color: '#424242' }}>{payslip.role}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Breakdown */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold mb-3" style={{ color: branding.customization.accentColor }}>
                  {t('Earnings')}
                </h3>
                <div>
                  <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                    <span className="text-sm" style={{ color: '#4b5563' }}>{t('Gross Salary')}</span>
                    <span className="text-sm font-semibold" style={{ color: '#424242' }}>
                      {currencySymbol} {(payslip.basicSalary || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  {overtimePay > 0 && (
                    <div className="border-b" style={{ borderColor: '#e5e7eb' }}>
                      <button
                        type="button"
                        onClick={() => setOtBreakdownOpen(o => !o)}
                        aria-expanded={otBreakdownOpen}
                        aria-controls="ot-breakdown-panel"
                        className="w-full flex justify-between items-center py-2 text-left"
                      >
                        <span className="text-sm flex items-center" style={{ color: '#4b5563' }}>
                          <span aria-hidden="true" className="me-1.5" style={{ color: '#059669' }}>⏱</span>
                          {t('payroll.payslip.overtimePay', 'Overtime Pay')}
                          <LawRefHint refText={otCitation} />
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: '#059669' }}>
                            +{currencySymbol} {overtimePay.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                          <span className="text-xs" style={{ color: '#9ca3af' }} aria-hidden="true">
                            {otBreakdownOpen ? '▾' : '▸'}
                          </span>
                        </span>
                      </button>
                      {otBreakdownOpen && (
                        <div id="ot-breakdown-panel" className="mb-2 ms-2 ps-3 pb-2 space-y-1 border-s-2" style={{ borderColor: '#a7f3d0' }} data-testid="ot-breakdown">
                          {isKsaOvertime ? (
                            <>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.actualHourly', 'Actual hourly wage')}</span>
                                <span>{currencySymbol} {otActualHourly.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.basicHourly', 'Basic hourly wage')}</span>
                                <span>{currencySymbol} {otBasicHourly.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs font-medium" style={{ color: '#4b5563' }}>
                                <span>{t('payroll.payslip.overtime.otRatePerHour', 'OT rate / hour')}</span>
                                <span>{currencySymbol} {otRatePerHour.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.hours', 'Overtime hours')}</span>
                                <span>× {otTotalHours.toFixed(1)}</span>
                              </div>
                              <div className="flex justify-between text-xs font-semibold pt-1 border-t" style={{ color: '#059669', borderColor: '#e5e7eb' }}>
                                <span>{t('payroll.payslip.overtimePay', 'Overtime Pay')}</span>
                                <span>= {currencySymbol} {overtimePay.toFixed(2)}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.basicHourly', 'Basic hourly wage')}</span>
                                <span>{currencySymbol} {otBasicHourly.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.regularTier', 'Regular OT (×1.25)')}</span>
                                <span>{otRegularHours.toFixed(1)} {t('payroll.payslip.overtime.hoursShort', 'h')} · {currencySymbol} {(otRegularHours * otBasicHourly * 1.25).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs" style={{ color: '#6b7280' }}>
                                <span>{t('payroll.payslip.overtime.premiumTier', 'Premium OT (×1.50)')}</span>
                                <span>{otPremiumHours.toFixed(1)} {t('payroll.payslip.overtime.hoursShort', 'h')} · {currencySymbol} {(otPremiumHours * otBasicHourly * 1.5).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs font-semibold pt-1 border-t" style={{ color: '#059669', borderColor: '#e5e7eb' }}>
                                <span>{t('payroll.payslip.overtimePay', 'Overtime Pay')}</span>
                                <span>= {currencySymbol} {overtimePay.toFixed(2)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {includeVariablePay && allowances > 0 && (
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                      <span className="text-sm" style={{ color: '#4b5563' }}>{t('Variable Pay')}</span>
                      <span className="text-sm font-semibold" style={{ color: '#424242' }}>
                        {currencySymbol} {allowances.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                  {showSaudiSocialInsurance ? (
                    <>
                      <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                        <span className="text-sm" style={{ color: '#dc2626' }}>
                          {gosiAniEmployeeLabel}
                          <LawRefHint refText={t('payroll.payslip.gosi.aniLawRef', 'SI Law 1421/2000 Art. 19')} color="#b91c1c" />
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                          -{currencySymbol} {(gosiAniEmployee ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                        <span className="text-sm" style={{ color: '#dc2626' }}>
                          {gosiSanedEmployeeLabel}
                          <LawRefHint refText={t('payroll.payslip.gosi.sanedLawRef', 'SI Law 1421/2000')} color="#b91c1c" />
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                          -{currencySymbol} {(gosiSanedEmployee ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                        <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                          {totalSocialInsuranceLabel}
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                          -{currencySymbol} {totalSocialInsurance.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    </>
                  ) : pensionDeduction > 0 ? (
                    <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                      <span className="text-sm" style={{ color: '#dc2626' }}>
                        {t('payroll.payslip.pension', 'Pension Contribution')}
                        <LawRefHint refText={t('payroll.payslip.pension.gpssaLawRef', 'GPSSA FL 7/1999')} color="#b91c1c" />
                      </span>
                      <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                        -{currencySymbol} {pensionDeduction.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  ) : null}
                  {showHourlyStatutoryBase && (
                    <p
                      className="px-3 pb-1 text-xs tabular-nums"
                      style={{ color: '#6b7280' }}
                      title={hourlyStatutoryBaseHint}
                      data-testid="payslip-hourly-statutory-base"
                    >
                      {hourlyStatutoryBaseCaption}
                    </p>
                  )}
                  {cchiEmployeeShare != null && cchiEmployeeShare > 0 && (
                    <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                      <span className="text-sm" style={{ color: '#dc2626' }}>
                        {t('payslip.cchiEmployeeShare')}
                      </span>
                      <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                        -{currencySymbol} {cchiEmployeeShare.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                  {(adjAdditions > 0 || adjDeductions > 0) && (
                    <>
                      {adjAdditions > 0 && (
                        <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#D1FAE5' }}>
                          <span className="text-sm" style={{ color: branding.customization.accentColor }}>{t('Adjustment Addition')}</span>
                          <span className="text-sm font-semibold" style={{ color: branding.customization.accentColor }}>
                            +{currencySymbol} {adjAdditions.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      )}
                      {adjDeductions > 0 && (
                        <div className="flex justify-between py-2 px-3 rounded" style={{ backgroundColor: '#FEE2E2' }}>
                          <span className="text-sm" style={{ color: '#dc2626' }}>{t('Adjustment Deduction')}</span>
                          <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>
                            -{currencySymbol} {adjDeductions.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                        <span className="text-sm font-semibold" style={{ color: adjustments >= 0 ? branding.customization.accentColor : '#dc2626' }}>
                          {t('Adjustment Total')}
                        </span>
                        <span className="text-sm font-semibold" style={{ color: adjustments >= 0 ? branding.customization.accentColor : '#dc2626' }}>
                          {adjustments > 0 ? '+' : ''}{currencySymbol} {adjustments.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between py-2 border-t-2" style={{ borderColor: '#d1d5db' }}>
                    <span className="text-sm font-semibold" style={{ color: '#424242' }}>{t('Total')}</span>
                    <span className="text-sm font-semibold" style={{ color: branding.customization.accentColor }}>
                      {currencySymbol} {total.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Employer Obligations (computed) — HR view only.
                  Never rendered on the employee-facing payslip.
                  PR-6 Task 8 / Gap 6 — GOSI ANI/OH/SANED employer shares. */}
              {hasEmployerObligations && (
                <div className="mb-8">
                  <h3
                    className="text-sm font-semibold mb-2"
                    style={{ color: branding.customization.accentColor }}
                  >
                    {employerSectionTitle}
                  </h3>
                  {/* Disclaimer — mirrors KsaGosiPanel.tsx structure (role=note, bold title,
                      body line below). Colors stay neutral (no amber) so the disclaimer
                      doesn't visually fight the payslip's existing branded sections. */}
                  <div
                    role="note"
                    aria-label={employerDisclaimerTitle}
                    className="mb-3 rounded-md border border-border bg-muted/40 p-3"
                  >
                    <div className="text-xs font-semibold text-foreground">
                      {employerDisclaimerTitle}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {employerDisclaimerBody}
                    </div>
                  </div>
                  <div>
                    {gosiAniEmployer != null && (
                      <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                        <span className="text-sm" style={{ color: '#4b5563' }}>
                          {gosiAniLabel}
                          <LawRefHint refText={t('payroll.payslip.gosi.aniLawRef', 'SI Law 1421/2000 Art. 19')} />
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#424242' }}>
                          {currencySymbol} {fmtNum(gosiAniEmployer)}
                        </span>
                      </div>
                    )}
                    {gosiOhEmployer != null && (
                      <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                        <span className="text-sm" style={{ color: '#4b5563' }}>
                          {gosiOhLabel}
                          <LawRefHint refText={t('payroll.payslip.gosi.ohLawRef', 'SI Law 1421/2000')} />
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#424242' }}>
                          {currencySymbol} {fmtNum(gosiOhEmployer)}
                        </span>
                      </div>
                    )}
                    {gosiSanedEmployer != null && (
                      <div className="flex justify-between py-2 border-b" style={{ borderColor: '#e5e7eb' }}>
                        <span className="text-sm" style={{ color: '#4b5563' }}>
                          {gosiSanedLabel}
                          <LawRefHint refText={t('payroll.payslip.gosi.sanedLawRef', 'SI Law 1421/2000')} />
                        </span>
                        <span className="text-sm font-semibold" style={{ color: '#424242' }}>
                          {currencySymbol} {fmtNum(gosiSanedEmployer)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Net Pay */}
              <div
                className="rounded-lg p-6 text-center"
                style={{ backgroundColor: netPayBgColor }}
              >
                <h3 className="text-sm font-semibold mb-2" style={{ color: netPayTextColor }}>{t('Net Pay')}</h3>
                <div className="text-2xl font-bold" style={{ color: netPayTextColor }}>
                  {currencySymbol} {netPay.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <p className="text-xs mt-1" style={{ color: netPaySubTextColor }}>
                  {t('Amount to be credited to your bank account')}
                </p>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t text-center" style={{ borderColor: '#d1d5db' }}>
                {branding.footerSettings.footerText && (
                  <p className="text-[9px]" style={{ color: branding.footerSettings.footerColor }}>
                    {branding.footerSettings.footerText}
                  </p>
                )}
                {branding.footerSettings.includeDisclaimer && branding.footerSettings.disclaimerText && (
                  <p className="text-[8px] mt-1 italic" style={{ color: branding.footerSettings.footerColor }}>
                    {branding.footerSettings.disclaimerText}
                  </p>
                )}
                {branding.footerSettings.showHelpContact && branding.footerSettings.helpContact && (
                  <p className="text-[9px] mt-1" style={{ color: branding.footerSettings.footerColor }}>
                    {t('For queries, contact')}: {branding.footerSettings.helpContact}
                  </p>
                )}
                <p className="text-[9px] mt-1" style={{ color: branding.footerSettings.footerColor }}>
                  {t('Generated on')} {new Date().toLocaleDateString('en-GB')} {t('at')} {new Date().toLocaleTimeString('en-GB')}
                </p>
              </div>

              {/* Watermark */}
              {branding.customization.showWatermark && branding.customization.watermarkText && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10"
                  style={{
                    fontSize: '4rem',
                    fontWeight: 'bold',
                    color: branding.headerSettings.headerColor,
                    transform: 'rotate(-45deg)'
                  }}
                >
                  {branding.customization.watermarkText}
                </div>
              )}
              </div>{/* close p-8 wrapper */}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default PayslipPreviewModal;
